import { EventProperties } from '@segment/analytics-next';

import { valueof } from '../../typescript';
import { AffiliatePageEvent, AffiliatePageEventDataType } from './affiliate';
import { AirdropLeaderboardEvent, AirdropLeaderboardEventDataType } from './airdrop-leaderboard';
import { AppDownloadDataType, appDownloadEvent } from './app-download';
import { AssetAllocationDataType, AssetAllocationEvent } from './asset-allocation';
import { AssetsEvent, AssetsEventDataType } from './assets';
import { BlogEvent, BlogEventDataType } from './blog';
import { ChartDataType, ChartEvent } from './chart';
import { CommunityEvent, CommunityEventDataType } from './community';
import { CompetitionsEvent, CompetitionsEventDataType } from './competitions';
import { DeviceDataType, deviceEvent } from './device';
import { DisplayPnlEvent, DisplayPnlEventDataType } from './display-pnl';
import { EarnEvent, EarnEventDataType } from './earn';
import { EventsEvent, EventsEventDataType } from './events';
import { FaqEvent, FaqEventDataType } from './faq';
import { FooterEvent, FooterEventDataType } from './footer';
import { GlossaryEvent, GlossaryEventDataType } from './glossary';
import { GNBDataType, GNBEvent } from './gnb';
import {
  HistoryDataType,
  HistoryDepositDataType,
  HistoryDepositEvent,
  HistoryEarnDataType,
  HistoryEarnEvent,
  HistoryEvent,
  HistoryLaunchpoolDataType,
  HistoryLaunchpoolEvent,
  HistoryPositionDataType,
  HistoryPositionEvent,
} from './history';
import { LossProtectionEvent, LossProtectionEventDataType } from './loss-protection';
import { LowestCostEvent, LowestCostEventDataType } from './lowest-cost';
import { MainEvent, MainEventDataType } from './main';
import { MarginModeEvent } from './margin-mode';
import { MarketEvent, MarketEventDataType } from './market';
import { MarketingDataType, MarketingEvent, MarketingEventDataType, marketingEventEvent } from './marketing';
import { NotificationEvent, NotificationEventDataType } from './notification';
import { NotificationCenterEvent, NotificationCenterEventDataType } from './notification-center';
import { PartnershipEvent, PartnershipEventDataType } from './partnership';
import { PerformanceEvent } from './performance';
import { PipEvent, PipEventDataType } from './pip';
import { PnlAnalysisEvent } from './pnl-analysis';
import { PoliciesEvent, PoliciesEventDataType } from './policies';
import { PositionEvent, PositionEventDataType } from './position';
import { ProfileEvent, ProfileEventDataType } from './profile';
import { ProfitBoostEventDataType } from './profit-boost';
import { RefereePageDataType, RefereePageEvent } from './referee';
import { ReferralPageEvent, ReferralPageEventDataType } from './referral';
import { RestrictedRegionEvent, RestrictedRegionEventDataType } from './restricted-region';
import { RewardEvent, RewardEventDataType } from './reward';
import { KycDataType, KycEvent, ReferralDataType, ReferralEvent, SessionDataType, SessionEvent } from './session';
import { SparkEvent, SparkEventDataType } from './spark';
import { SparkAuctionEvent, SparkAuctionEventDataType } from './spark-auction';
import { SquadCompetitionEvent } from './squad-competition';
import { StakeEvent, StakeEventDataType } from './stake';
import { SupportEvent, SupportEventDataType } from './support';
import { TradeEvent, TradeEventDataType } from './trade';
import { Trade3mToEarnDataType, Trade3mToEarnEvent } from './trade-3m-to-earn';
import { TradeModeEvent, TradeModeEventDataType } from './trade-mode';
import { TradeToEarnDataType, TradeToEarnEvent } from './trade-to-earn';
import { TradingIdeaFeedEvent, TradingIdeaFeedEventDataType } from './trading-idea-feed';
import { TradingLeaderboardEvent, TradingLeaderboardEventDataType } from './trading-leaderboard';
import { Usd1PointsEvent, Usd1PointsEventDataType } from './usd1-points';
import { UsdtPremiumEvent, UsdtPremiumEventDataType } from './usdt-premium';
import { VaraEvent, VaraEventDataType } from './vara';
import {
  VipEvent,
  VipEventDataType,
  VipRequirementEvent,
  VipRequirementEventDataType,
  VipStatusEvent,
  VipTrialEvent,
  VipTrialEventDataType,
} from './vip';
import { VipBenefitEvent, VipBenefitEventDataType } from './vip-benefit';
import { WalletEvent, WalletEventDataType } from './wallet';
import { ZeroSpreadEvent } from './zero-spread';

export * from './chart';
export * from './marketing';
export * from './session';
export * from './wallet';
export * from './referral';

export type SegmentTypes =
  | valueof<
      typeof AffiliatePageEvent &
        typeof BlogEvent &
        typeof ChartEvent &
        typeof EventsEvent &
        typeof FaqEvent &
        typeof GlossaryEvent &
        typeof SessionEvent &
        typeof GNBEvent &
        typeof KycEvent &
        typeof PartnershipEvent &
        typeof PoliciesEvent &
        typeof ReferralEvent &
        typeof WalletEvent &
        typeof MarketingEvent &
        typeof HistoryEvent &
        typeof HistoryEarnEvent &
        typeof HistoryPositionEvent &
        typeof HistoryLaunchpoolEvent &
        typeof HistoryDepositEvent &
        typeof RestrictedRegionEvent &
        typeof TradingLeaderboardEvent &
        typeof SupportEvent &
        typeof MainEvent &
        typeof MarketEvent &
        typeof TradeEvent &
        typeof PositionEvent &
        typeof EarnEvent &
        typeof AirdropLeaderboardEvent &
        typeof StakeEvent &
        typeof RewardEvent &
        typeof VipEvent &
        typeof VipTrialEvent &
        typeof VipStatusEvent &
        typeof VipBenefitEvent &
        typeof VipRequirementEvent &
        typeof SparkEvent &
        typeof SparkAuctionEvent &
        typeof ReferralPageEvent &
        typeof LossProtectionEvent &
        typeof TradeToEarnEvent &
        typeof RefereePageEvent &
        typeof AssetAllocationEvent &
        typeof TradeModeEvent &
        typeof Trade3mToEarnEvent &
        typeof LowestCostEvent &
        typeof CompetitionsEvent &
        typeof PipEvent &
        typeof PnlAnalysisEvent &
        typeof AssetsEvent &
        typeof ProfileEvent &
        typeof TradingIdeaFeedEvent &
        typeof CommunityEvent &
        typeof deviceEvent &
        typeof FooterEvent &
        typeof UsdtPremiumEvent &
        typeof NotificationEvent &
        typeof NotificationCenterEvent &
        typeof VaraEvent &
        typeof ZeroSpreadEvent &
        typeof DisplayPnlEvent &
        typeof marketingEventEvent &
        typeof appDownloadEvent &
        typeof Usd1PointsEvent
    >
  | keyof PerformanceEvent
  | keyof SquadCompetitionEvent
  | keyof MarginModeEvent;

export interface SegmentDataType
  extends AffiliatePageEventDataType,
    BlogEventDataType,
    ChartDataType,
    EventsEventDataType,
    FaqEventDataType,
    GlossaryEventDataType,
    SessionDataType,
    GNBDataType,
    PartnershipEventDataType,
    PoliciesEventDataType,
    ReferralDataType,
    MarketingDataType,
    HistoryDataType,
    HistoryEarnDataType,
    HistoryPositionDataType,
    HistoryLaunchpoolDataType,
    HistoryDepositDataType,
    RestrictedRegionEventDataType,
    TradingLeaderboardEventDataType,
    SupportEventDataType,
    MainEventDataType,
    MarketEventDataType,
    EventProperties,
    TradeEventDataType,
    PositionEventDataType,
    EarnEventDataType,
    StakeEventDataType,
    RewardEventDataType,
    VipEventDataType,
    VipTrialEventDataType,
    AirdropLeaderboardEventDataType,
    WalletEventDataType,
    VipBenefitEventDataType,
    VipRequirementEventDataType,
    SparkEventDataType,
    SparkAuctionEventDataType,
    ReferralPageEventDataType,
    LossProtectionEventDataType,
    KycDataType,
    TradeToEarnDataType,
    RefereePageDataType,
    AssetAllocationDataType,
    TradeModeEventDataType,
    Trade3mToEarnDataType,
    LowestCostEventDataType,
    CompetitionsEventDataType,
    AssetsEventDataType,
    ProfileEventDataType,
    TradingIdeaFeedEventDataType,
    CommunityEventDataType,
    ProfitBoostEventDataType,
    PipEventDataType,
    DeviceDataType,
    FooterEventDataType,
    UsdtPremiumEventDataType,
    NotificationEventDataType,
    NotificationCenterEventDataType,
    VaraEventDataType,
    PerformanceEvent,
    DisplayPnlEventDataType,
    MarketingEventDataType,
    AppDownloadDataType,
    SquadCompetitionEvent,
    DisplayPnlEventDataType,
    Usd1PointsEventDataType,
    MarginModeEvent {}
